import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopicCategory, TOPIC_CATEGORY_LABELS, getAllTopicCategories } from '../constants/topicCategories';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  Search as MagnifyingGlassIcon,
  Refresh as ArrowPathIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudDone as CloudDoneIcon,
  CloudSync as CloudSyncIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  AutoAwesome as GeneratorIcon,
  CloudUpload as BulkUploadIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import CompetitorManagement from '../components/CompetitorManagement';
import CompetitorModal from '../components/CompetitorModal';
import SalesPlaybookManagement from '../components/SalesPlaybookManagement';
import CompanyContextCard from '../components/CompanyContextCard';
import KnowledgeBaseTabs, {
  KnowledgeBaseTabId,
  useVisibleKnowledgeBaseTabs
} from '../components/knowledgeBase/KnowledgeBaseTabs';
import DiscoveryPlaybookTab from '../components/knowledgeBase/DiscoveryPlaybookTab';
import { uiConfig } from '../config';
import {
  getObjectionsGrouped,
  getFAQsGrouped,
  addObjection,
  updateObjection,
  deleteObjection,
  addObjectionVariation,
  addFAQ,
  addFAQVariation,
  updateFAQ,
  deleteFAQ,
  generateObjectionVariations,
  generateFAQVariations,
  getLandmines,
  addLandmine,
  updateLandmine,
  deleteLandmine,
  toggleLandmineStatus,
  generateLandmineVariations,
  getCompetitors,
  deleteCompetitor,
  searchDictionary,
  type DictionaryItem,
  type DictionaryVariation,
  type PaginationInfo,
  type LandmineItem,
  type CompetitorItem
} from '../services/apiService';

interface QuestionGroup extends DictionaryItem {
  isExpanded?: boolean;
}

const KnowledgeBase: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<KnowledgeBaseTabId>('objections');
  const { visibleTabs, defaultTab, loading: featuresLoading } = useVisibleKnowledgeBaseTabs();
  const [landmineSubTab, setLandmineSubTab] = useState<'legal_risk' | 'detractor'>('legal_risk');
  const [items, setItems] = useState<QuestionGroup[]>([]);
  const [landmines, setLandmines] = useState<LandmineItem[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVariationModalOpen, setIsVariationModalOpen] = useState(false);
  const [isGeneratedVariationsModalOpen, setIsGeneratedVariationsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DictionaryItem | null>(null);
  const [selectedParent, setSelectedParent] = useState<DictionaryItem | null>(null);
  const [generatedVariations, setGeneratedVariations] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: TopicCategory.OTHER as string,  // Category field now used for topic categorization
    isOriginal: true
  });
  const [landmineFormData, setLandmineFormData] = useState<Partial<LandmineItem>>({
    trigger: '',
    triggerVariations: [],
    landmineType: 'legal_risk',
    category: '',
    priority: 'medium',
    canSay: [],
    cannotSay: [],
    quickTips: '',
    isActive: true
  });
  const [editingLandmine, setEditingLandmine] = useState<LandmineItem | null>(null);
  const [isLandmineModalOpen, setIsLandmineModalOpen] = useState(false);
  const [triggerVariationInput, setTriggerVariationInput] = useState('');
  const [savingLandmine, setSavingLandmine] = useState(false);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [landmineModalError, setLandmineModalError] = useState<string | null>(null);
  const [variationFormData, setVariationFormData] = useState({
    question: ''
  });

  // Competitor-specific state
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorItem | null>(null);
  const [isCompetitorModalOpen, setIsCompetitorModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  const [savingVariations, setSavingVariations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemsPerPage = uiConfig.defaultPageSize;

  // Search state
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<QuestionGroup[]>([]);
  const [searchPagination, setSearchPagination] = useState<PaginationInfo | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);

  // Multi-select state
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [selectedLandmineIds, setSelectedLandmineIds] = useState<Set<string>>(new Set());
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Selection handlers for objections/FAQs
  const handleToggleItem = (itemId: string, item: QuestionGroup) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        // Deselecting original - also deselect all variations
        newSet.delete(itemId);
        item.variations?.forEach(v => newSet.delete(v._id));
      } else {
        // Selecting original - also select all variations
        newSet.add(itemId);
        item.variations?.forEach(v => newSet.add(v._id));
      }
      return newSet;
    });
  };

  const handleToggleVariation = (variationId: string) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const handleToggleLandmine = (landmineId: string) => {
    setSelectedLandmineIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(landmineId)) {
        newSet.delete(landmineId);
      } else {
        newSet.add(landmineId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    const count = activeTab === 'landmines' ? selectedLandmineIds.size :
                  activeTab === 'competitors' ? selectedCompetitorIds.size :
                  selectedItemIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} item${count !== 1 ? 's' : ''}?`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      if (activeTab === 'landmines') {
        // Delete landmines
        for (const id of selectedLandmineIds) {
          await deleteLandmine(id);
        }
        setSelectedLandmineIds(new Set());
        setSuccess(`Successfully deleted ${count} landmine${count !== 1 ? 's' : ''}`);
      } else if (activeTab === 'competitors') {
        // Delete competitors
        for (const id of selectedCompetitorIds) {
          await deleteCompetitor(id);
        }
        setSelectedCompetitorIds(new Set());
        setSuccess(`Successfully deleted ${count} competitor${count !== 1 ? 's' : ''}`);
      } else {
        // Delete objections/FAQs
        // Separate originals and variations
        const originalIdsToDelete = new Set<string>();
        const variationIdsToDelete = new Set<string>();

        items.forEach(item => {
          if (selectedItemIds.has(item._id)) {
            // This is an original item that's selected
            originalIdsToDelete.add(item._id);
          } else {
            // Check if any variations are selected
            item.variations?.forEach(variation => {
              if (selectedItemIds.has(variation._id)) {
                variationIdsToDelete.add(variation._id);
              }
            });
          }
        });

        let deletedCount = 0;

        // Delete originals first (backend will cascade delete their variations)
        for (const id of originalIdsToDelete) {
          try {
            if (activeTab === 'objections') {
              await deleteObjection(id);
            } else {
              await deleteFAQ(id);
            }
            deletedCount++;
          } catch (err) {
            console.error(`Failed to delete item ${id}:`, err);
          }
        }

        // Delete standalone variations (variations whose parent wasn't deleted)
        for (const id of variationIdsToDelete) {
          try {
            if (activeTab === 'objections') {
              await deleteObjection(id);
            } else {
              await deleteFAQ(id);
            }
            deletedCount++;
          } catch (err) {
            console.error(`Failed to delete variation ${id}:`, err);
          }
        }

        setSelectedItemIds(new Set());

        if (originalIdsToDelete.size > 0 && variationIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${originalIdsToDelete.size} original item${originalIdsToDelete.size !== 1 ? 's' : ''} and ${variationIdsToDelete.size} variation${variationIdsToDelete.size !== 1 ? 's' : ''}`);
        } else if (originalIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${originalIdsToDelete.size} original item${originalIdsToDelete.size !== 1 ? 's' : ''} and their variations`);
        } else if (variationIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${variationIdsToDelete.size} variation${variationIdsToDelete.size !== 1 ? 's' : ''}`);
        }
      }
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete some items');
      console.error(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Land on a tab that actually exists for this tenant.
  //
  // The initial state is 'objections', which is hidden whenever liveIntelligence
  // is off — the Lushin default. Without this correction an admin would open the
  // Knowledge Base to an empty page with no tab selected and no way to recover.
  // Runs once the flags have resolved, and only if the current tab is not among
  // the visible ones, so it never fights a deliberate click.
  useEffect(() => {
    if (featuresLoading) return;
    if (visibleTabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(defaultTab);
  }, [featuresLoading, visibleTabs, activeTab, defaultTab]);

  // Clear selections when tab changes
  useEffect(() => {
    setSelectedItemIds(new Set());
    setSelectedLandmineIds(new Set());
    setSelectedCompetitorIds(new Set());
    // Reset search when tab changes
    setSearchTerm('');
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchPagination(null);
    setSearchCurrentPage(1);
  }, [activeTab]);

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Fetch data when component mounts, tab changes, or page changes
  useEffect(() => {
    let isSubscribed = true;
    
    const fetchData = async () => {
      if (!isSubscribed) return;

      // Don't fetch until the capability flags have resolved.
      //
      // The initial tab is 'objections', which for a post-call-only tenant is
      // hidden AND 404s (the route is gated on liveIntelligence). Firing it
      // anyway put a request in flight that the tab-correction effect then
      // orphaned: the cleanup set isSubscribed=false, so the `finally` below
      // skipped setLoading(false), and `loading` stayed true forever — which
      // disables every tab button. The page rendered but no tab could be
      // clicked.
      if (featuresLoading) return;

      // Nor for a tab this tenant cannot see. The initial state is 'objections',
      // which the correction effect is about to replace — fetching for it would
      // 404 against a route gated on liveIntelligence and put an orphaned
      // request in flight for no reason.
      if (!visibleTabs.some((tab) => tab.id === activeTab)) {
        setLoading(false);
        return;
      }

      // Company Context and Discovery Playbook fetch their own data. Settle the
      // page-level flag explicitly rather than just returning, so a switch to
      // one of them always clears a spinner an orphaned request left behind.
      if (activeTab === 'companyContext' || activeTab === 'playbook') {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        if (activeTab === 'objections') {
          const response = await getObjectionsGrouped(currentPage, itemsPerPage);
          if (isSubscribed) {
            const groupedItems = (response?.objections || []).map(item => ({
              ...item,
              isExpanded: false
            }));
            setItems(groupedItems);
            setPagination(response?.pagination || null);
          }
        } else if (activeTab === 'faqs') {
          const response = await getFAQsGrouped(currentPage, itemsPerPage);
          if (isSubscribed) {
            const groupedItems = (response?.faqs || []).map(item => ({
              ...item,
              isExpanded: false
            }));
            setItems(groupedItems);
            setPagination(response?.pagination || null);
          }
        } else if (activeTab === 'landmines') {
          const response = await getLandmines(currentPage, itemsPerPage);
          if (isSubscribed) {
            // Sort landmines alphabetically by trigger
            const sortedLandmines = (response.landmines || []).sort((a, b) =>
              a.trigger.localeCompare(b.trigger)
            );
            setLandmines(sortedLandmines);
            setPagination(response.pagination || null);
          }
        } else if (activeTab === 'competitors') {
          const response = await getCompetitors(currentPage, itemsPerPage);
          if (isSubscribed) {
            // Sort competitors alphabetically by name (trigger field)
            const sortedCompetitors = (response?.competitors || []).sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            setCompetitors(sortedCompetitors);
            setPagination(response?.pagination || null);
          }
        }
      } catch (err) {
        if (isSubscribed) {
          setError('Failed to fetch dictionary items');
          console.error(err);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };
    
    fetchData();

    // Cleanup function to prevent state updates if component unmounts or effect re-runs
    return () => {
      isSubscribed = false;
    };
  }, [activeTab, currentPage, itemsPerPage, featuresLoading, visibleTabs]);

  // Clear search term when switching to tabs without search
  useEffect(() => {
    if (activeTab === 'landmines' || activeTab === 'competitors') {
      setSearchTerm('');
    }
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'objections') {
        const response = await getObjectionsGrouped(currentPage, itemsPerPage);
        const groupedItems = (response?.objections || []).map(item => ({
          ...item,
          isExpanded: false
        }));
        setItems(groupedItems);
        setPagination(response?.pagination || null);
      } else if (activeTab === 'faqs') {
        const response = await getFAQsGrouped(currentPage, itemsPerPage);
        const groupedItems = (response?.faqs || []).map(item => ({
          ...item,
          isExpanded: false
        }));
        setItems(groupedItems);
        setPagination(response?.pagination || null);
      } else if (activeTab === 'landmines') {
        const response = await getLandmines(currentPage, itemsPerPage);
        // Sort landmines alphabetically by trigger
        const sortedLandmines = (response.landmines || []).sort((a, b) =>
          a.trigger.localeCompare(b.trigger)
        );
        setLandmines(sortedLandmines);
        setPagination(response.pagination || null);
      } else if (activeTab === 'competitors') {
        const response = await getCompetitors(currentPage, itemsPerPage);
        // Sort competitors alphabetically by name
        const sortedCompetitors = (response?.competitors || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCompetitors(sortedCompetitors);
        setPagination(response?.pagination || null);
      }
    } catch (err) {
      setError('Failed to fetch dictionary items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId: string) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item._id === itemId
          ? { ...item, isExpanded: !item.isExpanded }
          : item
      )
    );
  };

  // Handle search with debouncing
  const handleSearch = async (query: string, page: number = 1) => {
    // Only perform search for objections and FAQs tabs
    if (activeTab !== 'objections' && activeTab !== 'faqs') {
      return;
    }

    if (!query.trim() || query.trim().length < 2) {
      // Empty or too-short search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchPagination(null);
      setSearchCurrentPage(1);
      return;
    }

    setSearchLoading(true);
    setError(null);

    try {
      const type = activeTab === 'objections' ? 'objection' : 'faq';
      const response = await searchDictionary(query, type, page, itemsPerPage);

      if (response.success) {
        // Add isExpanded property to search results
        const resultsWithExpanded = response.results.map(item => ({
          ...item,
          isExpanded: false
        }));
        setSearchResults(resultsWithExpanded);
        setSearchPagination(response.pagination);
        setSearchCurrentPage(page);
        setIsSearchMode(true);
      } else {
        setError(response.error || 'Search failed');
        setIsSearchMode(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search');
      setIsSearchMode(false);
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Only set up debouncing for objections and FAQs
    if (activeTab !== 'objections' && activeTab !== 'faqs') {
      return;
    }

    const debounceTimer = setTimeout(() => {
      handleSearch(searchTerm, 1);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, activeTab]);

  const handleAdd = () => {
    if (activeTab === 'landmines') {
      setEditingLandmine(null);
      setLandmineFormData({
        trigger: '',
        triggerVariations: [],
        landmineType: 'legal_risk',
        category: '',
        priority: 'medium',
        canSay: [],
        cannotSay: [],
        quickTips: '',
        isActive: true
      });
      setTriggerVariationInput('');
      setLandmineModalError(null);
      setIsLandmineModalOpen(true);
    } else if (activeTab === 'competitors') {
      setEditingCompetitor(null);
      setIsCompetitorModalOpen(true);
    } else {
      setEditingItem(null);
      setFormData({
        question: '',
        answer: '',
        category: TopicCategory.OTHER as string,
        isOriginal: true
      });
      setIsModalOpen(true);
    }
  };

  const handleEdit = (item: DictionaryItem) => {
    setEditingItem(item);
    setFormData({
      question: item.question,
      answer: item.answer,
      category: item.category || TopicCategory.OTHER as string,  // Use existing category or default to OTHER
      isOriginal: item.isOriginal || false
    });
    setIsModalOpen(true);
  };

  const handleAddVariation = (parent: DictionaryItem) => {
    setSelectedParent(parent);
    setVariationFormData({
      question: ''
    });
    setIsVariationModalOpen(true);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (editingItem) {
        // Update existing item
        if (activeTab === 'objections') {
          await updateObjection(editingItem._id, formData);
        } else {
          await updateFAQ(editingItem._id, formData);
        }
        setSuccess('Item updated successfully. Embeddings will be regenerated automatically.');
      } else {
        // Add new item
        let result: any;
        const itemType = activeTab === 'objections' ? 'objection' : 'FAQ';

        if (activeTab === 'objections') {
          result = await addObjection(formData.question, formData.answer, formData.category, formData.isOriginal);
        } else {
          result = await addFAQ(formData.question, formData.answer, formData.category);
        }

        if (result?.variationCount > 0) {
          setSuccess(`Created the original ${itemType} along with ${result.variationCount} variations.`);
        } else {
          setSuccess(`Created the original ${itemType}. You can create variations for this by clicking the Generate AI Variations button in the knowledge base.`);
        }
      }

      setIsSubmitting(false);
      setIsModalOpen(false);

      // For new items, reset to page 1 and fetch immediately
      if (!editingItem) {
        // If we're not on page 1, reset to it
        if (currentPage !== 1) {
          setCurrentPage(1);  // This will trigger useEffect
        } else {
          // If already on page 1, manually fetch to see the new item
          fetchItems();
        }
      } else {
        // For edits, just refresh current page
        fetchItems();
      }

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setIsSubmitting(false);
      setError('Failed to save item');
      console.error(err);
    }
  };

  const handleVariationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedParent) return;

    try {
      if (activeTab === 'objections') {
        await addObjectionVariation(selectedParent._id, variationFormData.question);
      } else {
        await addFAQVariation(selectedParent._id, variationFormData.question);
      }
      setSuccess('Variation added successfully. Embeddings will be generated automatically.');
      setIsVariationModalOpen(false);
      // Note: Variations are added to existing originals, so we don't need to reset page
      fetchItems();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError('Failed to add variation');
      console.error(err);
    }
  };

  const handleGenerateVariations = async (item: DictionaryItem) => {
    setError(null);
    setGeneratingFor(item._id);

    try {
      const result: any = activeTab === 'objections'
        ? await generateObjectionVariations(item._id, 3)
        : await generateFAQVariations(item._id, 3);

      if (result && result.success && result.variations) {
        setSelectedParent(item);
        // Clean up variations - remove surrounding quotes if present
        const cleanedVariations = result.variations.map((v: string) => {
          // Remove surrounding double quotes if the entire string is wrapped in them
          if (v.startsWith('"') && v.endsWith('"')) {
            return v.slice(1, -1);
          }
          return v;
        });
        setGeneratedVariations(cleanedVariations);
        setIsGeneratedVariationsModalOpen(true);
      } else if (result && result.variations) {
        // Handle direct response format from backend
        setSelectedParent(item);
        // Clean up variations - remove surrounding quotes if present
        const cleanedVariations = result.variations.map((v: string) => {
          // Remove surrounding double quotes if the entire string is wrapped in them
          if (v.startsWith('"') && v.endsWith('"')) {
            return v.slice(1, -1);
          }
          return v;
        });
        setGeneratedVariations(cleanedVariations);
        setIsGeneratedVariationsModalOpen(true);
      } else {
        setError('Failed to generate variations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate variations');
      console.error(err);
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleEditLandmine = (landmine: LandmineItem) => {
    setEditingLandmine(landmine);
    setLandmineFormData(landmine);
    setTriggerVariationInput('');
    setLandmineModalError(null);
    setIsLandmineModalOpen(true);
  };

  const handleDeleteLandmine = async (id: string, trigger: string) => {
    if (!window.confirm(`Are you sure you want to delete the landmine "${trigger}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteLandmine(id);
      setSuccess('Landmine deleted successfully');
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete landmine');
      console.error(err);
    }
  };

  const handleToggleLandmineStatus = async (landmine: LandmineItem) => {
    try {
      await toggleLandmineStatus(landmine._id);
      setSuccess(`"${landmine.trigger}" status updated`);
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to toggle landmine status');
      console.error(err);
    }
  };

  const handleAddTriggerVariation = () => {
    const variation = triggerVariationInput.trim();
    if (variation && !(landmineFormData.triggerVariations || []).includes(variation)) {
      setLandmineFormData({
        ...landmineFormData,
        triggerVariations: [...(landmineFormData.triggerVariations || []), variation]
      });
      setTriggerVariationInput('');
    }
  };

  const handleDeleteTriggerVariation = (variation: string) => {
    setLandmineFormData({
      ...landmineFormData,
      triggerVariations: (landmineFormData.triggerVariations || []).filter(v => v !== variation)
    });
  };

  const handleGenerateTriggerVariations = async () => {
    const trigger = landmineFormData.trigger?.trim();
    if (!trigger) {
      setLandmineModalError('Please enter a trigger word/phrase first');
      return;
    }

    setGeneratingVariations(true);
    setLandmineModalError(null);

    try {
      const variations = await generateLandmineVariations(trigger);
      if (variations && variations.length > 0) {
        // Add generated variations to existing ones, avoiding duplicates
        const existingVariations = landmineFormData.triggerVariations || [];
        const newVariations = variations.filter(
          v => !existingVariations.includes(v.toLowerCase()) && v.toLowerCase() !== trigger.toLowerCase()
        );
        setLandmineFormData({
          ...landmineFormData,
          triggerVariations: [...existingVariations, ...newVariations.map(v => v.toLowerCase())]
        });
        setSuccess(`Generated ${newVariations.length} variation${newVariations.length !== 1 ? 's' : ''}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setLandmineModalError('No variations could be generated. Try a different trigger phrase.');
      }
    } catch (err) {
      console.error('Error generating variations:', err);
      setLandmineModalError('Failed to generate variations. Please try again.');
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleLandmineSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLandmineModalError(null);

    // Validation
    if (!landmineFormData.trigger?.trim()) {
      setLandmineModalError('Trigger word/phrase is required');
      return;
    }

    // Category is only required for legal_risk type
    if (landmineFormData.landmineType === 'legal_risk' && !landmineFormData.category?.trim()) {
      setLandmineModalError('Category is required for Legal Risk landmines');
      return;
    }

    try {
      setSavingLandmine(true);
      // Filter out empty fields before submitting
      const processedData = {
        ...landmineFormData,
        triggerVariations: (landmineFormData.triggerVariations || []).filter(v => v.trim()),
        // Only include canSay/cannotSay for legal_risk type
        canSay: landmineFormData.landmineType === 'legal_risk'
          ? (landmineFormData.canSay || []).filter(item => item.point.trim())
          : [],
        cannotSay: landmineFormData.landmineType === 'legal_risk'
          ? (landmineFormData.cannotSay || []).filter(item => item.point.trim())
          : [],
        // Clear category for detractor type
        category: landmineFormData.landmineType === 'detractor' ? undefined : landmineFormData.category
      };

      if (editingLandmine) {
        await updateLandmine(editingLandmine._id, processedData);
        setSuccess('Landmine updated successfully');
      } else {
        await addLandmine(processedData);
        setSuccess('Landmine added successfully');
      }

      setIsLandmineModalOpen(false);
      setLandmineModalError(null);
      fetchItems();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error saving landmine:', err);

      // Handle duplicate key error
      const errorText = err?.error || err?.message || '';
      if (errorText.includes('E11000') ||
          errorText.includes('duplicate key') ||
          errorText.includes('already exists')) {
        setLandmineModalError('A landmine with this trigger already exists. Please use a different trigger word.');
      } else {
        setLandmineModalError(err?.message || err?.error || 'Failed to save landmine. Please try again.');
      }
    } finally {
      setSavingLandmine(false);
    }
  };

  const handleSaveGeneratedVariations = async () => {
    if (!selectedParent || savingVariations) return;
    
    setSavingVariations(true);
    setError(null);
    let savedCount = 0;
    
    try {
      for (const variation of generatedVariations) {
        if (variation.trim()) {
          try {
            if (activeTab === 'objections') {
              await addObjectionVariation(selectedParent._id, variation);
            } else {
              await addFAQVariation(selectedParent._id, variation);
            }
            savedCount++;
          } catch (err) {
            console.error('Error saving variation:', err);
          }
        }
      }
      
      if (savedCount > 0) {
        setSuccess(`Successfully saved ${savedCount} variation${savedCount !== 1 ? 's' : ''}`);
        setIsGeneratedVariationsModalOpen(false);
        setGeneratedVariations([]);
        fetchItems();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError('No variations were saved');
      }
    } catch (err) {
      setError('Failed to save variations');
      console.error(err);
    } finally {
      setSavingVariations(false);
    }
  };

  // Determine which items to display
  // For objections and FAQs: use search results if in search mode, otherwise use regular items
  const displayItems = (activeTab === 'objections' || activeTab === 'faqs')
    ? (isSearchMode ? searchResults : items)
    : items;

  return (
    <MainLayout title="Knowledge Base Management">
      <div className="p-2">
        <div className="mb-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
            <p className="text-gray-600 mt-1">
              Manage company context, objections, FAQs, compliance landmines, competitor mentions, and sales playbooks. Configure detection triggers and guidance for real-time sales conversations.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => navigate('/knowledge-generator')}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              data-tutorial="knowledge-generator"
            >
              <GeneratorIcon className="mr-2" style={{ fontSize: '1.25rem' }} />
              Knowledge Generator
            </button>
            <button
              onClick={() => navigate('/bulk-upload')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              data-tutorial="bulk-upload"
            >
              <BulkUploadIcon className="mr-2" style={{ fontSize: '1.25rem' }} />
              Bulk Upload
            </button>
          </div>
        </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="text-green-600 mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-green-800">{success}</span>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <XCircleIcon className="text-red-600 mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Tabs — declared once in KnowledgeBaseTabs and filtered by capability
          flag, so the objection/FAQ/landmine/competitor set stays hidden for
          post-call-only tenants without six near-identical conditionals here. */}
      {/* Deliberately NOT disabled while loading. Switching tabs mid-fetch is
          already safe — the fetch effect's isSubscribed guard discards stale
          responses — and tying tab availability to a fetch flag means any bug
          that leaves it set locks the user out of the whole page. */}
      <KnowledgeBaseTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Filters and Actions - hide for tabs that have their own controls */}
      {activeTab !== 'salesplaybooks' && activeTab !== 'companyContext' && activeTab !== 'playbook' && (
        <div className="mb-4 flex flex-col sm:flex-row items-start gap-4">
          {/* Only show search for objections and FAQs */}
          {(activeTab === 'objections' || activeTab === 'faqs') ? (
            <div className="flex-1 w-full sm:w-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: '1.25rem' }} />
                <input
                  type="text"
                  placeholder="Search questions and answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <CloseIcon style={{ fontSize: '1.25rem' }} />
                  </button>
                )}
              </div>
              {isSearchMode && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    <MagnifyingGlassIcon style={{ fontSize: '0.875rem' }} className="mr-1" />
                    Search Results
                  </span>
                  <span className="text-xs text-gray-600">
                    {searchPagination?.total || 0} result{(searchPagination?.total || 0) !== 1 ? 's' : ''} found
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1"></div>
          )}

          <div className="flex flex-wrap gap-4 shrink-0">

          {/* Bulk Delete Button - Show when items are selected */}
          {((activeTab === 'objections' || activeTab === 'faqs') && selectedItemIds.size > 0) ||
           (activeTab === 'landmines' && selectedLandmineIds.size > 0) ||
           (activeTab === 'competitors' && selectedCompetitorIds.size > 0) ? (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 ${
                bulkDeleting
                  ? 'bg-red-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {bulkDeleting ? (
                <ArrowPathIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
              ) : (
                <TrashIcon style={{ fontSize: '1.25rem' }} />
              )}
              {bulkDeleting ? 'Deleting...' : `Delete ${
                activeTab === 'landmines' ? selectedLandmineIds.size :
                activeTab === 'competitors' ? selectedCompetitorIds.size :
                selectedItemIds.size
              } item${((
                activeTab === 'landmines' ? selectedLandmineIds.size :
                activeTab === 'competitors' ? selectedCompetitorIds.size :
                selectedItemIds.size
              ) !== 1) ? 's' : ''}`}
            </button>
          ) : null}

          <button
            onClick={fetchItems}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
            Refresh
          </button>

          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon style={{ fontSize: '1.25rem' }} />
            Add {activeTab === 'landmines' ? 'Landmine' : activeTab === 'competitors' ? 'Competitor' : `Original ${activeTab === 'objections' ? 'Objection' : 'FAQ'}`}
          </button>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {activeTab === 'companyContext' ? (
          // Company Context tab — single-form editor, manages its own loading
          // and save state internally. Does not share the page's items-loading
          // spinner because the data shape is completely different.
          <div className="p-4">
            <CompanyContextCard />
          </div>
        ) : activeTab === 'playbook' ? (
          // Discovery Playbook — same reasoning as Company Context: its own
          // fetch, dirty-tracking and save, on a data shape nothing else here
          // shares.
          <div className="p-4">
            <DiscoveryPlaybookTab />
          </div>
        ) : (loading || searchLoading) ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-gray-400" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-gray-500">{searchLoading ? 'Searching...' : 'Loading...'}</p>
          </div>
        ) : activeTab === 'landmines' ? (
          // Landmines display with sub-tabs
          <div>
            {/* Sub-tabs for Legal Risk and Detractor */}
            <div className="border-b border-gray-200 bg-gray-50 px-4">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setLandmineSubTab('legal_risk')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    landmineSubTab === 'legal_risk'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Legal Risk ({landmines.filter(l => l.landmineType !== 'detractor').length})
                </button>
                <button
                  onClick={() => setLandmineSubTab('detractor')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    landmineSubTab === 'detractor'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Detractors ({landmines.filter(l => l.landmineType === 'detractor').length})
                </button>
              </nav>
            </div>

            {/* Filtered landmines list */}
            {landmines.filter(l => landmineSubTab === 'detractor' ? l.landmineType === 'detractor' : l.landmineType !== 'detractor').length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No {landmineSubTab === 'detractor' ? 'detractor' : 'legal risk'} landmines found. Add a new landmine to get started.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {landmines
                  .filter(l => landmineSubTab === 'detractor' ? l.landmineType === 'detractor' : l.landmineType !== 'detractor')
                  .map((landmine) => (
                  <div key={landmine._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 mr-4">
                        {/* Checkbox for selection */}
                        <input
                          type="checkbox"
                          checked={selectedLandmineIds.has(landmine._id)}
                          onChange={() => handleToggleLandmine(landmine._id)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-sm font-medium text-gray-900">
                              {landmine.trigger}
                            </h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              landmine.priority === 'high' ? 'bg-red-100 text-red-800' :
                              landmine.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {landmine.priority}
                            </span>
                            {landmine.landmineType !== 'detractor' && landmine.category && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {landmine.category}
                              </span>
                            )}
                            {landmine.isActive ? (
                              <CheckCircleIcon className="text-green-500" style={{ fontSize: '1rem' }} />
                            ) : (
                              <XCircleIcon className="text-gray-400" style={{ fontSize: '1rem' }} />
                            )}
                          </div>

                          {landmine.triggerVariations && landmine.triggerVariations.length > 0 && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-gray-600">Variations: </span>
                              <span className="text-sm text-gray-600">{landmine.triggerVariations.join(', ')}</span>
                            </div>
                          )}

                          {landmine.quickTips && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-gray-600">
                                {landmine.landmineType === 'detractor' ? 'Say Instead: ' : 'Quick Tips: '}
                              </span>
                              <span className={`text-sm ${landmine.landmineType === 'detractor' ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                {landmine.landmineType === 'detractor' ? `"${landmine.quickTips}"` : landmine.quickTips}
                              </span>
                            </div>
                          )}

                          {landmine.landmineType !== 'detractor' && (
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              {landmine.canSay && landmine.canSay.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-green-700 mb-1">Can Say:</p>
                                  <ul className="text-xs text-gray-600 list-disc list-inside">
                                    {landmine.canSay.slice(0, 2).map((item, idx) => (
                                      <li key={idx}>{item.point}</li>
                                    ))}
                                    {landmine.canSay.length > 2 && (
                                      <li className="text-gray-400">+{landmine.canSay.length - 2} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}

                              {landmine.cannotSay && landmine.cannotSay.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-red-700 mb-1">Cannot Say:</p>
                                  <ul className="text-xs text-gray-600 list-disc list-inside">
                                    {landmine.cannotSay.slice(0, 2).map((item, idx) => (
                                      <li key={idx}>{item.point}</li>
                                    ))}
                                    {landmine.cannotSay.length > 2 && (
                                      <li className="text-gray-400">+{landmine.cannotSay.length - 2} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleLandmineStatus(landmine)}
                          className={`px-3 py-1 rounded text-sm ${
                            landmine.isActive
                              ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {landmine.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditLandmine(landmine)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon style={{ fontSize: '1.25rem' }} />
                        </button>
                        <button
                          onClick={() => handleDeleteLandmine(landmine._id, landmine.trigger)}
                          className="p-1 text-red-600 hover:text-red-900"
                          title="Delete landmine"
                        >
                          <TrashIcon style={{ fontSize: '1.25rem' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'competitors' ? (
          // Competitors display - moved here into main conditional
          <CompetitorManagement
            competitors={competitors}
            searchTerm={searchTerm}
            onRefresh={fetchItems}
            onEdit={(competitor) => {
              setEditingCompetitor(competitor);
              setIsCompetitorModalOpen(true);
            }}
            setError={setError}
            setSuccess={setSuccess}
            selectedCompetitorIds={selectedCompetitorIds}
            onToggleCompetitor={(competitorId: string) => {
              setSelectedCompetitorIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(competitorId)) {
                  newSet.delete(competitorId);
                } else {
                  newSet.add(competitorId);
                }
                return newSet;
              });
            }}
          />
        ) : activeTab === 'salesplaybooks' ? (
          // Sales Playbooks display
          <SalesPlaybookManagement
            setError={setError}
            setSuccess={setSuccess}
          />
        ) : displayItems.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {isSearchMode ? 'No results found. Try a different search term.' : 'No items found. Add a new item to get started.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      checked={displayItems.length > 0 && displayItems.every(item => {
                        const allIds = [item._id, ...(item.variations?.map(v => v._id) || [])];
                        return allIds.every(id => selectedItemIds.has(id));
                      })}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Select all
                          const newSet = new Set(selectedItemIds);
                          displayItems.forEach(item => {
                            newSet.add(item._id);
                            item.variations?.forEach(v => newSet.add(v._id));
                          });
                          setSelectedItemIds(newSet);
                        } else {
                          // Deselect all
                          setSelectedItemIds(new Set());
                        }
                      }}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-1/3">Question</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 flex-1">Answer</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {displayItems.map((item) => (
                  <React.Fragment key={item._id}>
                    {/* Main row */}
                    <tr
                      onClick={() => item.variations && item.variations.length > 0 && toggleExpand(item._id)}
                      className={`${item.variations && item.variations.length > 0 ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}
                    >
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.has(item._id)}
                          onChange={() => handleToggleItem(item._id, item)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {item.variations && item.variations.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(item._id);
                              }}
                              className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                              title={item.isExpanded ? "Collapse" : "Expand variations"}
                            >
                              {item.isExpanded ? <ExpandLessIcon style={{ fontSize: '1.25rem' }} /> : <ExpandMoreIcon style={{ fontSize: '1.25rem' }} />}
                            </button>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 break-words">{item.question}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {item.isOriginal ? (
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                  Original
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                  Variation
                                </span>
                              )}
                              {item.category && TOPIC_CATEGORY_LABELS[item.category as TopicCategory] && (
                                <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                  {TOPIC_CATEGORY_LABELS[item.category as TopicCategory]}
                                </span>
                              )}
                              {item.variations && item.variations.length > 0 && (
                                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                  {item.variations.length} variation{item.variations.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {/* Embedding Status */}
                              {item.needsEmbedding ? (
                                <span className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full" title="Embedding pending">
                                  <CloudSyncIcon style={{ fontSize: '0.875rem' }} />
                                  Pending
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full" title="Embedding ready">
                                  <CloudDoneIcon style={{ fontSize: '0.875rem' }} />
                                  Ready
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <p className="text-gray-700 line-clamp-3 break-words">{item.answer}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* Hide AI generation button in search mode */}
                          {item.isOriginal && !isSearchMode && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateVariations(item);
                              }}
                              className="text-purple-600 hover:text-purple-900 p-1"
                              title="Generate AI variations"
                              disabled={generatingFor === item._id}
                            >
                              {generatingFor === item._id ? (
                                <ArrowPathIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
                              ) : (
                                <AutoAwesomeIcon style={{ fontSize: '1.25rem' }} />
                              )}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <PencilIcon style={{ fontSize: '1.25rem' }} />
                          </button>
                          {item.isOriginal && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddVariation(item);
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                              title="Add new variation"
                            >
                              <PlusIcon style={{ fontSize: '0.875rem' }} />
                              Add Variation
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Variations rows (shown when expanded) */}
                    {item.isExpanded && item.variations && item.variations.length > 0 ? (
                      <>
                        {item.variations.map((variation: DictionaryVariation) => (
                          <tr key={variation._id} className="bg-blue-50 border-l-4 border-blue-400 hover:bg-blue-100 transition-colors">
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedItemIds.has(variation._id)}
                                onChange={() => handleToggleVariation(variation._id)}
                                disabled={selectedItemIds.has(item._id)}
                                className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${
                                  selectedItemIds.has(item._id) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                title={selectedItemIds.has(item._id) ? 'This variation will be deleted with its parent' : undefined}
                              />
                            </td>
                            <td colSpan={3} className="px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full font-medium">
                                      Variation
                                    </span>
                                    {/* Embedding Status for Variation */}
                                    {variation.needsEmbedding ? (
                                      <span className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full" title="Embedding pending">
                                        <CloudSyncIcon style={{ fontSize: '0.75rem' }} />
                                        Pending
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full" title="Embedding ready">
                                        <CloudDoneIcon style={{ fontSize: '0.75rem' }} />
                                        Ready
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-700"><strong>Question:</strong> {variation.question}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      handleEdit({
                                        _id: variation._id,
                                        question: variation.question,
                                        answer: item.answer,
                                        category: '',
                                        isOriginal: false,
                                        parentId: item._id
                                      });
                                    }}
                                    className="text-blue-600 hover:text-blue-900 p-1"
                                    title="Edit variation"
                                  >
                                    <PencilIcon style={{ fontSize: '1rem' }} />
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {/* Use search pagination when in search mode, otherwise use regular pagination */}
        {/* Hide pagination for tabs that aren't paginated (single-form tabs) */}
        {(() => {
          // Don't show pagination for Sales Playbooks tab or Company Context tab
          // (both are single-form / self-managed and have no underlying paged list).
          if (activeTab === 'salesplaybooks' || activeTab === 'companyContext' || activeTab === 'playbook') return null;

          const activePagination = isSearchMode ? searchPagination : pagination;
          const activePage = isSearchMode ? searchCurrentPage : currentPage;
          const setActivePage = isSearchMode
            ? (page: number) => handleSearch(searchTerm, page)
            : setCurrentPage;

          if (!activePagination) return null;

          return (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700 flex items-center gap-2">
                {isSearchMode && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    <MagnifyingGlassIcon style={{ fontSize: '0.875rem' }} className="mr-1" />
                    Search Results
                  </span>
                )}
                <span>
                  Showing {((activePage - 1) * itemsPerPage) + 1} to {Math.min(activePage * itemsPerPage, activePagination?.total || 0)} of {activePagination?.total || 0} {activeTab === 'landmines' ? 'landmines' : activeTab === 'competitors' ? 'competitors' : isSearchMode ? 'results' : 'original questions'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setActivePage(Math.max(1, activePage - 1))}
                  disabled={activePage === 1}
                  className={`px-3 py-1 rounded ${
                    activePage === 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, activePagination?.totalPages || 1) }, (_, i) => {
                  let pageNum: number;
                  const totalPages = activePagination?.totalPages || 1;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (activePage <= 3) {
                    pageNum = i + 1;
                  } else if (activePage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = activePage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setActivePage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        activePage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setActivePage(Math.min(activePagination?.totalPages || 1, activePage + 1))}
                  disabled={activePage === (activePagination?.totalPages || 1)}
                  className={`px-3 py-1 rounded ${
                    activePage === (activePagination?.totalPages || 1)
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingItem ? 
                  (editingItem.isOriginal === false ? 'Edit Variation' : 'Edit Original') 
                  : 'Add Original'
                } {activeTab === 'objections' ? 'Objection' : 'FAQ'}
              </h2>
              
              <form onSubmit={handleSubmit}>
                {editingItem && editingItem.isOriginal === false && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Variations share the same answer as their parent question. 
                      Editing the answer here will update it for all variations of this question.
                    </p>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question
                  </label>
                  <textarea
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Answer {editingItem && editingItem.isOriginal === false && '(Shared with parent)'}
                  </label>
                  <textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows={5}
                    required
                    disabled={!!(editingItem && editingItem.isOriginal === false)}
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic Category {editingItem && editingItem.isOriginal === false && '(Shared with parent)'}
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    disabled={!!(editingItem && editingItem.isOriginal === false)}
                  >
                    {getAllTopicCategories().map(cat => (
                      <option key={cat} value={cat}>
                        {TOPIC_CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting ? (editingItem ? 'Updating...' : 'Adding...') : (editingItem ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Variation Modal */}
      {isVariationModalOpen && selectedParent && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                Add Variation to {activeTab === 'objections' ? 'Objection' : 'FAQ'}
              </h2>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Original Question:</p>
                <p className="text-sm text-gray-600">{selectedParent.question}</p>
                <p className="text-sm font-medium text-gray-700 mt-3 mb-1">Answer:</p>
                <p className="text-sm text-gray-600">{selectedParent.answer}</p>
              </div>
              
              <form onSubmit={handleVariationSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variation Question
                  </label>
                  <textarea
                    value={variationFormData.question}
                    onChange={(e) => setVariationFormData({ ...variationFormData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Enter a variation of the original question..."
                    required
                  />
                </div>

                {/* Category field removed - variations are always marked as non-original */}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsVariationModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Variation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Landmine Add/Edit Modal */}
      {isLandmineModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[calc(90vh-100px)] flex flex-col">
            {/* Fixed Header */}
            <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingLandmine ? 'Edit Landmine' : 'Add New Landmine'}
              </h2>
            </div>

            <form onSubmit={handleLandmineSubmit} className="flex flex-col flex-1 overflow-hidden">
              {/* Scrollable Content */}
              <div className="p-6 pt-4 overflow-y-auto flex-1">
                {/* Error Display */}
                {landmineModalError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-red-900 mb-1">Error</h3>
                        <p className="text-sm text-red-800">{landmineModalError}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLandmineModalError(null)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trigger Word/Phrase <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={landmineFormData.trigger || ''}
                      onChange={(e) => setLandmineFormData({ ...landmineFormData, trigger: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder={landmineFormData.landmineType === 'detractor' ? "e.g., I understand, Trust me" : "e.g., FDA approved, guaranteed, cure"}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={landmineFormData.landmineType || 'legal_risk'}
                      onChange={(e) => setLandmineFormData({
                        ...landmineFormData,
                        landmineType: e.target.value as 'legal_risk' | 'detractor',
                        // Reset type-specific fields when switching
                        priority: e.target.value === 'detractor' ? 'high' : landmineFormData.priority,
                        category: e.target.value === 'detractor' ? '' : landmineFormData.category
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="legal_risk">Legal Risk</option>
                      <option value="detractor">Detractor</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {landmineFormData.landmineType === 'detractor'
                        ? 'Phrases that negatively impact sales conversations'
                        : 'Compliance and legal risk phrases'}
                    </p>
                  </div>

                  {landmineFormData.landmineType === 'legal_risk' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={landmineFormData.category || ''}
                        onChange={(e) => setLandmineFormData({ ...landmineFormData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Medical Claims, Legal Terms, Pricing"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={landmineFormData.priority || 'medium'}
                      onChange={(e) => setLandmineFormData({ ...landmineFormData, priority: e.target.value as 'high' | 'medium' | 'low' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      disabled={landmineFormData.landmineType === 'detractor'}
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    {landmineFormData.landmineType === 'detractor' && (
                      <p className="text-xs text-gray-500 mt-1">Detractors are always high priority</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Trigger Variations
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateTriggerVariations}
                      disabled={generatingVariations || !landmineFormData.trigger?.trim()}
                      className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {generatingVariations ? (
                        <>
                          <ArrowPathIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <AutoAwesomeIcon style={{ fontSize: '0.875rem' }} />
                          Generate with AI
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Add variation manually... (Press Enter)"
                      value={triggerVariationInput}
                      onChange={(e) => setTriggerVariationInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddTriggerVariation();
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddTriggerVariation}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {(landmineFormData.triggerVariations || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {(landmineFormData.triggerVariations || []).map((variation, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {variation}
                          <button
                            type="button"
                            onClick={() => handleDeleteTriggerVariation(variation)}
                            className="hover:text-blue-900"
                          >
                            <CloseIcon style={{ fontSize: '0.875rem' }} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Variations help detect different ways users might say the trigger phrase
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {landmineFormData.landmineType === 'detractor' ? 'Corrective Phrase' : 'Quick Tips'}
                    {landmineFormData.landmineType === 'detractor' && <span className="text-red-500"> *</span>}
                  </label>
                  <textarea
                    value={landmineFormData.quickTips || ''}
                    onChange={(e) => setLandmineFormData({ ...landmineFormData, quickTips: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    placeholder={landmineFormData.landmineType === 'detractor'
                      ? "What to say instead (e.g., 'Help me understand what matters most here')"
                      : "Brief guidance or context for this landmine"}
                    required={landmineFormData.landmineType === 'detractor'}
                  />
                  {landmineFormData.landmineType === 'detractor' && (
                    <p className="text-xs text-gray-500 mt-1">This phrase will be shown as the recommended alternative</p>
                  )}
                </div>

                {landmineFormData.landmineType === 'legal_risk' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      What CAN Be Said
                    </label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {(landmineFormData.canSay || []).map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-2">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.point}
                              onChange={(e) => {
                                const newCanSay = [...(landmineFormData.canSay || [])];
                                newCanSay[index] = { ...newCanSay[index], point: e.target.value };
                                setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Approved phrase"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newCanSay = (landmineFormData.canSay || []).filter((_, i) => i !== index);
                                setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                              }}
                              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              ×
                            </button>
                          </div>
                          <input
                            type="text"
                            value={item.context || ''}
                            onChange={(e) => {
                              const newCanSay = [...(landmineFormData.canSay || [])];
                              newCanSay[index] = { ...newCanSay[index], context: e.target.value };
                              setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                            }}
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Context (optional)"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newCanSay = [...(landmineFormData.canSay || []), { point: '', context: '' }];
                          setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 w-full"
                      >
                        + Add Approved Phrase
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      What CANNOT Be Said
                    </label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {(landmineFormData.cannotSay || []).map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-2">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.point}
                              onChange={(e) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], point: e.target.value };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Prohibited phrase"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newCannotSay = (landmineFormData.cannotSay || []).filter((_, i) => i !== index);
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              ×
                            </button>
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={item.reason || ''}
                              onChange={(e) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], reason: e.target.value };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Reason (optional)"
                            />
                            <select
                              value={item.severity || 'general'}
                              onChange={(e) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], severity: e.target.value as 'general' | 'legal_risk' | 'compliance_risk' | 'brand_risk' };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="general">General</option>
                              <option value="legal_risk">Legal Risk</option>
                              <option value="compliance_risk">Compliance Risk</option>
                              <option value="brand_risk">Brand Risk</option>
                            </select>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newCannotSay = [...(landmineFormData.cannotSay || []), { point: '', reason: '', severity: 'general' as 'general' | 'legal_risk' | 'compliance_risk' | 'brand_risk' }];
                          setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 w-full"
                      >
                        + Add Prohibited Phrase
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Fixed Footer */}
              <div className="p-6 pt-4 border-t border-gray-200 flex-shrink-0 bg-white">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsLandmineModalOpen(false)}
                    disabled={savingLandmine}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingLandmine}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingLandmine ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>{editingLandmine ? 'Update' : 'Add'} Landmine</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generated Variations Review Modal */}
      {isGeneratedVariationsModalOpen && selectedParent && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <AutoAwesomeIcon className="text-purple-600" />
                Review AI Generated Variations
              </h2>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Original Question:</p>
                <p className="text-sm text-gray-600 mb-2">{selectedParent.question}</p>
                <p className="text-sm font-medium text-gray-700 mb-1">Answer:</p>
                <p className="text-sm text-gray-600">{selectedParent.answer}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Generated Variations (edit as needed):
                </p>
                <div className="space-y-3">
                  {generatedVariations.map((variation, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 mt-2">{index + 1}.</span>
                      <textarea
                        value={variation}
                        onChange={(e) => {
                          const newVariations = [...generatedVariations];
                          newVariations[index] = e.target.value;
                          setGeneratedVariations(newVariations);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                      />
                      <button
                        onClick={() => {
                          const newVariations = generatedVariations.filter((_, i) => i !== index);
                          setGeneratedVariations(newVariations);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Remove variation"
                      >
                        <TrashIcon style={{ fontSize: '1.25rem' }} />
                      </button>
                    </div>
                  ))}
                </div>
                
                {generatedVariations.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    All variations have been removed. Click cancel to close without saving.
                  </p>
                )}
              </div>
              
              <div className="flex justify-between items-center">
                <button
                  onClick={() => {
                    const newVariation = '';
                    setGeneratedVariations([...generatedVariations, newVariation]);
                  }}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <PlusIcon style={{ fontSize: '1rem' }} />
                  Add Another
                </button>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsGeneratedVariationsModalOpen(false);
                      setGeneratedVariations([]);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveGeneratedVariations}
                    disabled={savingVariations || generatedVariations.length === 0 || generatedVariations.every(v => !v.trim())}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      savingVariations || generatedVariations.length === 0 || generatedVariations.every(v => !v.trim())
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {savingVariations ? (
                      <>
                        <ArrowPathIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                        Saving...
                      </>
                    ) : (
                      <>Save {generatedVariations.filter(v => v.trim()).length} Variation{generatedVariations.filter(v => v.trim()).length !== 1 ? 's' : ''}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Competitor Modal */}
      <CompetitorModal
        isOpen={isCompetitorModalOpen}
        onClose={() => {
          setIsCompetitorModalOpen(false);
          setEditingCompetitor(null);
        }}
        competitor={editingCompetitor}
        onSuccess={fetchItems}
        setSuccess={setSuccess}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </MainLayout>
  );
};

export default KnowledgeBase;